import { config } from "./config";
import { tokenManager } from "./auth/tokenManager";
import { isNative } from "@/utils/platform";
import { nativeTokenStore } from "./auth/nativeTokenStore";
import { z } from "zod";

export interface NotificationResponse {
  id: number;
  content: string;
  url: string;
  updated_at: string;
  is_read: boolean;
}

export interface NotificationSettings {
  settings: {
    [key: string]: {
      notifications: boolean;
      email: boolean;
      push?: boolean;
    };
  };
}

const DeviceTokenRequestSchema = z.object({
  token: z.string().min(1).max(512).regex(/^[A-Za-z0-9:_\-\.]+$/),
  platform: z.enum(['ios', 'android', 'web', 'unknown']),
  device_id: z.string().min(1).max(128).regex(/^[A-Za-z0-9_\-]+$/).optional(),
});

const DeviceTokenUnregisterSchema = z.object({
  token: z.string().min(1).max(512).regex(/^[A-Za-z0-9:_\-\.]+$/),
});

type DeviceTokenPlatform = z.infer<typeof DeviceTokenRequestSchema>['platform'];

class WebSocketClient {
  private ws: WebSocket | null = null;
  private messageHandlers: ((data: NotificationResponse) => void)[] = [];
  private shouldReconnect: boolean = false;
  private reconnectAttempt: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private currentUserId: number | null = null;
  private connecting: boolean = false;

  async connect(userId: number): Promise<void> {
    if (!userId || this.connecting) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.currentUserId = userId;
    this.shouldReconnect = true;
    this.connecting = true;

    try {
      const base = config.API_URL.replace(/^http/, 'ws');
      let url = `${base}/notification/${userId}/notifications/ws`;

      if (isNative()) {
        try {
          const access = await nativeTokenStore.getAccessToken();
          if (access) {
            const u = new URL(url);
            u.searchParams.set('token', access);
            url = u.toString();
          }
        } catch {
          // ignore - fall back to cookie auth
        }
      }

      this.ws = new WebSocket(url);

      this.ws.onmessage = (event) => {
        try {
          const raw = typeof event.data === 'string' ? event.data : '';
          if (!raw) return;
          const parsed = JSON.parse(raw);
          if (parsed?.event === 'ping') {
            try { this.ws?.send('pong'); } catch { /* ignore */ }
            return;
          }
          // Backend publishes RealtimeNotificationPayload (event: "notification.new",
          // created_at, type, ...). Map it to the NotificationResponse shape the UI
          // already renders so a divergent field name can't crash the tree.
          if (parsed?.event && parsed.event !== 'notification.new') {
            return;
          }
          if (typeof parsed?.id !== 'number' || typeof parsed?.content !== 'string') {
            return;
          }
          const timestamp =
            (typeof parsed.updated_at === 'string' && parsed.updated_at) ||
            (typeof parsed.created_at === 'string' && parsed.created_at) ||
            new Date().toISOString();
          const normalized: NotificationResponse = {
            id: parsed.id,
            content: parsed.content,
            url: typeof parsed.url === 'string' ? parsed.url : '',
            updated_at: timestamp,
            is_read: Boolean(parsed.is_read),
          };
          this.messageHandlers.forEach((h) => {
            try {
              h(normalized);
            } catch (e) {
              console.error('WS handler error', e);
            }
          });
        } catch (e) {
          console.warn('WS parse error', e);
        }
      };

      this.ws.onopen = () => {
        this.reconnectAttempt = 0;
      };

      this.ws.onclose = () => {
        this.ws = null;
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = () => {
        try { this.ws?.close(); } catch { /* ignore */ }
      };
    } catch (err) {
      console.warn('WS connect error', err);
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    } finally {
      this.connecting = false;
    }
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect || !this.currentUserId) return;
    if (this.reconnectTimer) return;
    const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(this.reconnectAttempt, 5)));
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect && this.currentUserId) {
        this.connect(this.currentUserId);
      }
    }, delay);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.currentUserId = null;
    this.reconnectAttempt = 0;
  }

  onMessage(handler: (data: NotificationResponse) => void) {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }
}

const wsClient = new WebSocketClient();

export const notificationApi = {
  connectWebSocket: (userId: number) => {
    void wsClient.connect(userId);
    return wsClient;
  },

  disconnectWebSocket: () => {
    wsClient.disconnect();
  },

  onNotification: (handler: (data: NotificationResponse) => void) => {
    return wsClient.onMessage(handler);
  },

  getNotifications: async (userId: number): Promise<NotificationResponse[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    return response.json();
  },

  markAsRead: async (userId: number, notificationId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/${notificationId}/mark-read`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error('Failed to mark notification as read');
    }
  },

  markAllAsRead: async (userId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/mark-all-read`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error('Failed to mark all notifications as read');
    }
  },

  getSettings: async (userId: number): Promise<NotificationSettings> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/settings`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch notification settings');
    }

    return response.json();
  },

  updateSettings: async (userId: number, settings: NotificationSettings): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/settings`,
      {
        method: 'POST',
        body: JSON.stringify(settings),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update notification settings');
    }
  },

  registerDeviceToken: async (
    userId: number,
    token: string,
    platform: DeviceTokenPlatform,
    deviceId?: string
  ): Promise<{ success: boolean; id?: number }> => {
    const body = DeviceTokenRequestSchema.parse({
      token,
      platform,
      ...(deviceId ? { device_id: deviceId } : {}),
    });
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/device-tokens`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to register device token');
    }
    return response.json();
  },

  unregisterDeviceToken: async (
    userId: number,
    token: string
  ): Promise<{ success: boolean }> => {
    const body = DeviceTokenUnregisterSchema.parse({ token });
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/notification/${userId}/notifications/device-tokens`,
      {
        method: 'DELETE',
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to unregister device token');
    }
    return response.json();
  },
};
